import { useState } from "react";
import { MessageSquareReply, X } from "lucide-react";
import { toast } from "sonner";
import { patchCandidate } from "@/lib/scoutApi";
import { extractApiError } from "@/lib/api";

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

const ReplyBadge = ({ c, onClear }) => (
  <div
    data-testid="scout-reply-badge"
    className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs border ${c.reply_status === "replied" ? "border-[var(--accent)] bg-[var(--accent)]/10 text-[#0A192F]" : "border-slate-400 bg-slate-100 text-slate-700"}`}
  >
    <MessageSquareReply className="w-3.5 h-3.5" />
    <span className="uppercase tracking-wider font-medium">{c.reply_status}</span>
    <span className="text-slate-500">{fmt(c.replied_at)}</span>
    {c.reply_note && <span className="text-slate-600 italic">· {c.reply_note}</span>}
    <button type="button" data-testid="scout-reply-clear" onClick={onClear} aria-label="Clear reply" className="text-slate-400 hover:text-[#0A192F]">
      <X className="w-3 h-3" />
    </button>
  </div>
);

export const ReplyTracker = ({ c, onChange }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!["contacted", "booked"].includes(c.status) && !c.reply_status) return null;

  const log = async (reply_status) => {
    setBusy(true);
    try {
      onChange(await patchCandidate(c.id, { reply_status, reply_note: note }));
      setOpen(false);
      setNote("");
      if (reply_status) toast.success(reply_status === "replied" ? "Reply logged — nudges paused" : "Logged as declined");
    } catch (err) {
      toast.error(extractApiError(err, "Could not log reply."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4" data-testid="scout-reply-tracker">
      {c.reply_status ? (
        <ReplyBadge c={c} onClear={() => log("")} />
      ) : open ? (
        <div className="border border-slate-200 bg-slate-50 p-3 max-w-xl">
          <input
            data-testid="scout-reply-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            placeholder="What did they say? (optional)"
            className="w-full bg-white border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" data-testid="scout-reply-replied" disabled={busy} onClick={() => log("replied")}
              className="bg-[var(--accent)] hover:bg-[#0A192F] text-white px-4 py-2 text-xs font-medium transition-colors disabled:opacity-50">
              They replied
            </button>
            <button type="button" data-testid="scout-reply-declined" disabled={busy} onClick={() => log("declined")}
              className="border border-slate-400 hover:border-[#0A192F] px-4 py-2 text-xs font-medium transition-colors disabled:opacity-50">
              They declined
            </button>
            <button type="button" onClick={() => setOpen(false)} className="text-xs text-slate-500 hover:text-[#0A192F] px-2">Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" data-testid="scout-reply-open" onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#0A192F] px-4 py-2 text-xs font-medium transition-colors">
          <MessageSquareReply className="w-3.5 h-3.5" /> Log guest reply
        </button>
      )}
    </div>
  );
};

export default ReplyTracker;
